'use client';

import clsx from 'clsx';
import Lottie from 'lottie-react';
import { FC, SetStateAction, useEffect, useRef, useState } from 'react';

import {
	type CursorVariant,
	CustomCursorContext
} from '@/components/CustomCursorProvider/CustomCursorContext';
import Image from '@/components/Image';
import { gsap } from '@/lib/gsap';
import { BasicProps } from '@/types/common';

import css from './style.module.scss';

export type { CursorVariant };

interface Props extends BasicProps {
	mousemoveObserverSelector?: string;
}

const CustomCursorProvider: FC<Props> = ({ children, mousemoveObserverSelector }) => {
	const cursorRef = useRef<HTMLDivElement>(null);
	const isCursorVisibleRef = useRef(false);
	const wasFirstMoveRef = useRef(false);
	const [assetData, setAssetData] = useState<any>(null);
	const [cursorVariant, setCursorVariant] = useState<CursorVariant | null>(null);

	const actions = useRef({
		setIsCursorVisible: (value: SetStateAction<boolean>) => {
			const next = typeof value === 'function' ? value(isCursorVisibleRef.current) : value;
			isCursorVisibleRef.current = next;

			const cursor = cursorRef.current;
			if (!cursor || !wasFirstMoveRef.current) return;

			gsap.to(cursor, {
				opacity: next ? 1 : 0,
				duration: 0.3,
				ease: 'power2.out'
			});
		},
		setCursorVariant
	}).current;

	useEffect(() => {
		const loadAsset = async () => {
			switch (cursorVariant) {
				case 'lottie-green-visit-case':
					const lottieGreenVisitCaseAsset =
						await import('public/assets/lottie/cursors/green-visit-case.json');
					setAssetData(lottieGreenVisitCaseAsset.default);
					break;
				case 'lottie-empty-green':
					const lottieEmptyAsset =
						await import('public/assets/lottie/cursors/green-cursor-empty.json');
					setAssetData(lottieEmptyAsset.default);
					break;
				case 'lottie-green-explore-more':
					const lottieGreenExploreMoreAsset =
						await import('public/assets/lottie/cursors/green-explore-more.json');
					setAssetData(lottieGreenExploreMoreAsset.default);
					break;
				case 'lottie-purple-explore-more':
					const lottiePurpleExploreMoreAsset =
						await import('public/assets/lottie/cursors/purple-explore-more.json');
					setAssetData(lottiePurpleExploreMoreAsset.default);
					break;
				case 'lottie-article-green':
					const lottieArticleGreenAsset =
						await import('public/assets/lottie/cursors/green-cursor-article.json');
					setAssetData(lottieArticleGreenAsset.default);
					break;
				default:
					break;
			}
		};
		loadAsset();
	}, [cursorVariant]);

	useEffect(() => {
		const cursor = cursorRef.current;
		if (!cursor) return;

		gsap.set(cursor, { xPercent: -50, yPercent: -50 });

		let targetX = 0;
		let targetY = 0;
		let rafId: number | null = null;
		let isActive = true;

		const setX = gsap.quickTo(cursor, 'x', { duration: 0.3, ease: 'power3.out' });
		const setY = gsap.quickTo(cursor, 'y', { duration: 0.3, ease: 'power3.out' });

		const moveCursor = (e: MouseEvent) => {
			if (!isActive) return;
			targetX = e.clientX;
			targetY = e.clientY;
			setX(targetX);
			setY(targetY + window.scrollY);

			if (wasFirstMoveRef.current) return;

			wasFirstMoveRef.current = true;
			if (isCursorVisibleRef.current)
				gsap.to(cursor, { opacity: 1, duration: 0.3, ease: 'power2.out' });
		};

		const onScroll = () => {
			if (!isActive) return;
			if (rafId !== null) return;
			rafId = requestAnimationFrame(() => {
				setY(targetY + window.scrollY);
				rafId = null;
			});
		};

		let mousemoveObserverRef: HTMLElement | null = null;
		try {
			mousemoveObserverRef = document.querySelector(mousemoveObserverSelector ?? '');
		} catch {
			// ignore error and handle it in finally block
		} finally {
			if (!mousemoveObserverRef) mousemoveObserverRef = document.body;
		}

		const observer = new IntersectionObserver(
			([entry]) => {
				isActive = entry.isIntersecting;
			},
			{ threshold: 0 }
		);
		observer.observe(mousemoveObserverRef);

		mousemoveObserverRef.addEventListener('mousemove', moveCursor, { passive: true });
		window.addEventListener('scroll', onScroll, { passive: true });

		return () => {
			observer.disconnect();
			mousemoveObserverRef.removeEventListener('mousemove', moveCursor);
			window.removeEventListener('scroll', onScroll);
			if (rafId !== null) cancelAnimationFrame(rafId);
		};
	}, []);

	return (
		<CustomCursorContext.Provider value={actions}>
			{children}

			<div ref={cursorRef} className={css.cursorWrapper}>
				{assetData?.layers ? (
					<Lottie
						loop
						autoplay
						animationData={assetData}
						className={clsx(
							css.cursorAnim,
							cursorVariant === 'lottie-article-green' && css.rightDirection
						)}
					/>
				) : assetData?.src ? (
					<Image src={assetData.src} className={css.cursorImage} width={120} height={120} alt="" />
				) : null}
			</div>
		</CustomCursorContext.Provider>
	);
};

export default CustomCursorProvider;
